import {
  AvailableMultiRawValues,
  AvailableRawValues,
  AvailableSingleRawValues,
  ElementPropertyRuleConfig,
  MultiRawValues,
  MultiSampleValues,
  SampleValues,
  SingleRawValues,
  SingleSampleValues,
} from '../types';
import {
  ElementPropertyRule,
  ElementPropertyRuleRecord,
} from '@fullstory/proto/fs/services/search-settings';
import { assertExhaustive } from '@fullstory/typeutils';
import { compileRegexValueFormat, formatRegexValue, RegexValueFormat } from './regex';
import moment from 'moment';
import { GroupedObjectInfo } from '@fullstory/renderer-shared';

export function makeAvailableSingleRawValues(objects: {
  [path: string]: GroupedObjectInfo[];
}): AvailableSingleRawValues {
  const values: SingleRawValues[] = [];
  Object.values(objects).forEach(groupedObjects => {
    const object = groupedObjects[0];
    if (!object) {
      return;
    }
    // the leaf most selector rule should contain the attributes we can target
    const attributes = { ...object.selector.rule?.attrs };
    // id and class are special attributes
    const { id, classes } = object.selector.rule ?? {};
    if (id) {
      attributes['id'] = id;
    }
    if (classes?.length) {
      attributes['class'] = classes.join(' ');
    }
    values.push({ attributes, text: object.innerText });
  });
  return {
    type: 'single',
    values,
  };
}

export function makeAvailableMultiRawValues(objects: {
  [path: string]: GroupedObjectInfo[];
}): AvailableMultiRawValues {
  const values: MultiRawValues[] = [];
  Object.values(objects).forEach(groupedObjects => {
    const attributes: { [attr: string]: string[] } = {};
    const addAttr = (attr: string, value: string) => {
      const attrs = attributes[attr] ?? [];
      attrs.push(value);
      attributes[attr] = attrs;
    };
    const text: string[] = [];
    for (const object of groupedObjects) {
      // the leaf most selector rule should contain the attributes we can target
      Object.entries(object.selector.rule?.attrs ?? {}).forEach(([attr, value]) => {
        addAttr(attr, value);
      });
      // id and class are special attributes
      const { id, classes } = object.selector.rule ?? {};
      if (id) {
        addAttr('id', id);
      }
      if (classes?.length) {
        addAttr('class', classes.join(' '));
      }
      if (object.innerText !== undefined) {
        text.push(object.innerText);
      }
    }
    values.push({ attributes, text });
  });
  return {
    type: 'multi',
    values,
  };
}

export function getSampleValues(
  elementPropertyRule: ElementPropertyRuleConfig,
  availableValues: AvailableRawValues | undefined,
): SampleValues<string> | undefined {
  const oneof = ElementPropertyRule.source_oneof(elementPropertyRule);
  if (!oneof || !availableValues) {
    return undefined;
  }
  return getSampleValuesForOneof(oneof, availableValues);
}

export function getSampleValuesForOneof(
  oneof: Exclude<ReturnType<typeof ElementPropertyRule.source_oneof>, null>,
  availableValues: AvailableRawValues,
): SampleValues<string> {
  return availableValues.type === 'single'
    ? getSingleSampleValues(oneof, availableValues.values)
    : getMultiSampleValues(oneof, availableValues.values);
}

export function getSingleSampleValues(
  oneof: Exclude<ReturnType<typeof ElementPropertyRule.source_oneof>, null>,
  rawValues: SingleRawValues[],
): SingleSampleValues<string> {
  const sampleValues: string[] = [];
  for (const values of rawValues) {
    switch (oneof.opt) {
      case ElementPropertyRule.source.attribute: {
        const val = values.attributes[oneof.val.attribute];
        if (val) {
          sampleValues.push(val);
        }
        break;
      }
      case ElementPropertyRule.source.innerText: {
        if (values.text) {
          sampleValues.push(values.text);
        }
        break;
      }
      case ElementPropertyRule.source.static: {
        if (oneof.val.value) {
          sampleValues.push(oneof.val.value);
        }
        break;
      }
      default:
        assertExhaustive(oneof);
    }
  }
  return {
    type: 'single',
    values: sampleValues,
  };
}

export function getMultiSampleValues(
  oneof: Exclude<ReturnType<typeof ElementPropertyRule.source_oneof>, null>,
  rawValues: MultiRawValues[],
): MultiSampleValues<string> {
  const sampleValues: string[][] = [];
  for (const values of rawValues) {
    switch (oneof.opt) {
      case ElementPropertyRule.source.attribute: {
        const vals = values.attributes[oneof.val.attribute];
        if (vals?.length) {
          sampleValues.push(vals);
        }
        break;
      }
      case ElementPropertyRule.source.innerText: {
        if (values.text.length) {
          sampleValues.push(values.text);
        }
        break;
      }
      case ElementPropertyRule.source.static: {
        if (oneof.val.value) {
          sampleValues.push([oneof.val.value]);
        }
        break;
      }
      default:
        assertExhaustive(oneof);
    }
  }
  return {
    type: 'multi',
    values: sampleValues,
  };
}

export function transformedValues(
  regex: ElementPropertyRuleConfig['regex'],
  values: SampleValues<string> | undefined,
): SampleValues<string> | undefined {
  const retValues = rawTransformedValues(regex, values);
  if (!retValues) {
    return undefined;
  }
  if (retValues.type === 'single') {
    return {
      type: 'single',
      values: retValues.values.filter((v): v is string => v !== undefined),
    };
  } else {
    return {
      type: 'multi',
      values: retValues.values.map(valueArray =>
        valueArray.filter((v): v is string => v !== undefined),
      ),
    };
  }
}

export function rawTransformedValues(
  regex: ElementPropertyRuleConfig['regex'],
  values: SampleValues<string> | undefined,
): SampleValues<string | undefined> | undefined {
  if (!regex?.expression || !values) {
    return values;
  }
  const { format, error } = compileRegexValueFormat(regex.format);
  if (error) {
    return { type: values.type, values: [] };
  }
  try {
    const expression = new RegExp(regex.expression);
    if (values.type === 'single') {
      return {
        type: 'single',
        values: values.values.map(value => transformedValue(expression, format, value)),
      };
    } else {
      return {
        type: 'multi',
        values: values.values.map(valueArray =>
          valueArray.map(value => transformedValue(expression, format, value)),
        ),
      };
    }
  } catch (e) {
    return { type: values.type, values: [] };
  }
}

export function transformedValue(
  expression: RegExp,
  format: RegexValueFormat,
  value: string,
): string | undefined {
  const result = expression.exec(value);
  if (!result) {
    return undefined;
  }
  const match = result[0];
  // NB: for some reason, some groups can come back undefined, even though the types say they should be strings
  const groups = result.slice(1).map(g => g ?? '');
  return formatRegexValue(format, match, groups);
}

const dateFormats: string[] = ['YYYY-MM-DD', 'YYYY-MM-DDTHH:MM:DDZ'];

export function rawValueAsDate(value: string): Date | undefined {
  for (const format of dateFormats) {
    const date = moment(value, format, true);
    if (date.isValid()) {
      return date.toDate();
    }
  }
  return undefined;
}

export function flattenSampleValues<T>(sampleValues: SampleValues<T> | undefined): T[] | undefined {
  if (!sampleValues) {
    return undefined;
  }
  return sampleValues.type === 'single' ? sampleValues.values : sampleValues.values.flat();
}

export function isV2CompatiblePropertyRule(
  elementPropertyRuleRecord: ElementPropertyRuleRecord,
): boolean {
  // right now, the v2 experience requires the container (either standalone or as a parent) is an element
  const hasElementContainer = !!elementPropertyRuleRecord.elementPropertyRule?.namedElementId;
  return hasElementContainer;
}
